/**
 * Quaternion conversion module.
 * Converts planner camera positions into the quaternion format expected by serialized
 * Jig stages while preserving the coordinate handedness used elsewhere in the runtime.
 */
/**
 * Quaternion format expected by Jig stage camera data.
 */
export interface ViewpointQuaternion {
  w: number
  x: number
  y: number
  z: number
}

/**
 * Builds a camera quaternion that looks from `position` toward `target`.
 * Inputs must already be in the coordinate space that will store the quaternion.
 */
export function lookAtToQuaternion(
  position: { x: number; y: number; z: number },
  target: { x: number; y: number; z: number }
): ViewpointQuaternion {
  let fx = target.x - position.x
  let fy = target.y - position.y
  let fz = target.z - position.z
  let fLen = Math.sqrt(fx * fx + fy * fy + fz * fz)

  if (fLen < 1e-10) {
    return { w: 1, x: 0, y: 0, z: 0 }
  }

  fx /= fLen
  fy /= fLen
  fz /= fLen

  let worldUpX = 0
  let worldUpY = 1
  let worldUpZ = 0

  const dotUp = Math.abs(fx * worldUpX + fy * worldUpY + fz * worldUpZ)
  if (dotUp > 0.9999) {
    worldUpX = 0
    worldUpY = 0
    worldUpZ = 1
  }

  let rx = worldUpY * fz - worldUpZ * fy
  let ry = worldUpZ * fx - worldUpX * fz
  let rz = worldUpX * fy - worldUpY * fx
  const rLen = Math.sqrt(rx * rx + ry * ry + rz * rz)
  rx /= rLen
  ry /= rLen
  rz /= rLen

  const ux = fy * rz - fz * ry
  const uy = fz * rx - fx * rz
  const uz = fx * ry - fy * rx

  const m00 = rx
  const m01 = ux
  const m02 = fx
  const m10 = ry
  const m11 = uy
  const m12 = fy
  const m20 = rz
  const m21 = uz
  const m22 = fz

  const trace = m00 + m11 + m22
  let w: number
  let x: number
  let y: number
  let z: number

  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1.0)
    w = 0.25 / s
    x = (m21 - m12) * s
    y = (m02 - m20) * s
    z = (m10 - m01) * s
  } else if (m00 > m11 && m00 > m22) {
    const s = 2.0 * Math.sqrt(1.0 + m00 - m11 - m22)
    w = (m21 - m12) / s
    x = 0.25 * s
    y = (m01 + m10) / s
    z = (m02 + m20) / s
  } else if (m11 > m22) {
    const s = 2.0 * Math.sqrt(1.0 + m11 - m00 - m22)
    w = (m02 - m20) / s
    x = (m01 + m10) / s
    y = 0.25 * s
    z = (m12 + m21) / s
  } else {
    const s = 2.0 * Math.sqrt(1.0 + m22 - m00 - m11)
    w = (m10 - m01) / s
    x = (m02 + m20) / s
    y = (m12 + m21) / s
    z = 0.25 * s
  }

  return {
    w: w,
    x: x,
    y: y,
    z: z,
  }
}

/**
 * Converts Three.js/viewer-space positions into the current stored Jig/web-viewer
 * viewpoint contract: keep X/Y and negate Z.
 */
export function threePositionToJigPosition(position: { x: number; y: number; z: number }): { x: number; y: number; z: number } {
  return {
    x: position.x,
    y: position.y,
    z: -position.z,
  }
}

/**
 * Computes the stored Jig-space look-at quaternion from Three.js/viewer-space
 * camera and target positions.
 */
export function threeLookAtToJigQuaternion(
  position: { x: number; y: number; z: number },
  target: { x: number; y: number; z: number }
): ViewpointQuaternion {
  return lookAtToQuaternion(
    threePositionToJigPosition(position),
    threePositionToJigPosition(target)
  )
}
